import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Landing from "@/pages/Landing";
import Loading from "@/pages/Loading";
import LoadingClient from "@/pages/client/LoadingClient";
import InvestorDashboard from "@/pages/investor/Dashboard";
import ClientDashboard from "@/pages/client/ClientDashboard";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import ProjectDetail from "@/pages/investor/ProjectDetail";
import InteractiveDocument from "@/pages/investor/InteractiveDocument";
import ClientProjectDetail from "@/pages/client/ClientProjectDetail";

function App() {
  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Test Supabase connection by getting the session (doesn't need tables)
    supabase.auth
      .getSession()
      .then(() => {
        setConnected(true);
        setChecking(false);
      })
      .catch((error) => {
        console.error("Connection error:", error);
        setChecking(false);
      });
  }, []);

  // Show connection status while checking
  if (checking || !connected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-4xl font-bold mb-4">TCGL Ventures Portal</h1>
          <p className="text-xl mb-6">Investor & Client Dashboard</p>
          <div className="flex items-center justify-center gap-2">
            <div
              className={`w-3 h-3 rounded-full ${
                checking
                  ? "bg-yellow-400"
                  : connected
                  ? "bg-green-400"
                  : "bg-red-400"
              }`}
            />
            <span className="text-sm">
              {checking
                ? "Checking Supabase..."
                : connected
                ? "Supabase Connected ✓"
                : "Connection Failed"}
            </span>
          </div>
          {!checking && !connected && (
            <p className="mt-4 text-sm text-red-200">
              Please check your environment variables and Supabase configuration
            </p>
          )}
        </div>
      </div>
    );
  }

  // Once connected, show the full app with routing
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/loading" element={<Loading />} />
        <Route path="/loading-client" element={<LoadingClient />} />

        {/* Protected Investor Routes */}
        <Route
          path="/investor/dashboard"
          element={
            <ProtectedRoute allowedUserTypes={["investor"]}>
              <InvestorDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/investor/project/:id"
          element={
            <ProtectedRoute allowedUserTypes={["investor"]}>
              <ProjectDetail />
            </ProtectedRoute>
          }
        />

        <Route
          path="/investor/project/:projectId/document/:documentId"
          element={
            <ProtectedRoute allowedUserTypes={["investor"]}>
              <InteractiveDocument />
            </ProtectedRoute>
          }
        />

        {/* Protected Client Routes */}
        <Route
          path="/client/dashboard"
          element={
            <ProtectedRoute allowedUserTypes={["client"]}>
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/project/:id"
          element={
            <ProtectedRoute allowedUserTypes={["client"]}>
              <ClientProjectDetail />
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
